import { action, makeObservable, observable, runInAction } from 'mobx';
import type { RootStore } from 'logic';

interface SnackbarAction {
  fn: () => void;
  text: string;
}

interface SnackbarDetails {
  fn?: () => void;
  actionText?: string;
  actions?: SnackbarAction[];
  description: string;
  timestamp: number;
}

export const MESSAGE_TIME_LIMIT_MS = 15 * 1000;

export class UiSnackbarStore {
  private rootStore: RootStore;
  private snackbarDetails?: SnackbarDetails;
  public remainingTime?: number;
  public isActionInProgress?: boolean;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeObservable<UiSnackbarStore, 'snackbarDetails' | 'checkRemainingTime'>(this, {
      snackbarDetails: observable.ref,
      remainingTime: observable,
      setSnackbarDetails: action.bound,
      checkRemainingTime: action.bound,
      reset: action.bound,
    });
  }

  init() {
    /*
    Note about cleaning up: these event listeners should be setup at most once throughout
    the lifecycle of the app, namely when the Root Store calls the init() method of this
    store. Since this store survives throughout the life of the app, it's fine that there's
    no cleanup for the moment. If the CQ app eventually becomes a modular part of a bigger
    system, stores will have cleanup() methods in addition to their init() methods, where
    events and reactions can be removed as necessary.
    */
    document.addEventListener(
      'keydown',
      action((e) => {
        // Using manual listener instead of mousetrap to ensure that undo works after interacting with input
        if (e.metaKey && e.key === 'z') {
          this.runSnackbarAction();
        }
      }),
    );
  }

  reset() {
    this.snackbarDetails = undefined;
    this.remainingTime = undefined;
  }

  private checkRemainingTime() {
    const { snackbarDetails } = this;
    if (!snackbarDetails) {
      return;
    }

    const remainingTime = Math.max(
      MESSAGE_TIME_LIMIT_MS - (Date.now() - snackbarDetails.timestamp),
      0,
    );
    if (remainingTime === 0) {
      this.reset();
    } else {
      this.remainingTime = remainingTime;
      requestAnimationFrame(this.checkRemainingTime);
    }
  }

  setUndoDetails(undoFn: () => unknown, description: string) {
    const undoShortcut =
      this.rootStore.uiKeyboardShortcutsStore.getShortcutCombinationByName('undo');
    const actionText = `Undo (${undoShortcut})`;

    return this.setSnackbarDetails({
      fn: undoFn,
      actionText,
      description,
    });
  }

  setSnackbarDetails({ fn, actionText, actions, description }: Omit<SnackbarDetails, 'timestamp'>) {
    // Store the function for undo even if snackbars are disabled
    this.snackbarDetails = {
      fn,
      actionText,
      actions,
      description,
      timestamp: Date.now(),
    };

    // Always start the cleanup timer, even if snackbar UI is disabled
    // This ensures undo state is properly cleared after MESSAGE_TIME_LIMIT_MS
    this.checkRemainingTime();

    return () => {
      if (
        this.snackbarDetails?.fn === fn ||
        this.snackbarDetails?.actions?.some((snackbarAction) => snackbarAction.fn === fn)
      ) {
        this.reset();
      }
    };
  }

  setUndoDetailsWithVisitAsset(
    undoFn: () => unknown,
    description: string,
    visitAssetFn: () => void,
    visitAssetText: string = 'Visit Card',
  ) {
    const undoShortcut =
      this.rootStore.uiKeyboardShortcutsStore.getShortcutCombinationByName('undo');
    const actionText = `Undo (${undoShortcut})`;

    return this.setSnackbarDetails({
      fn: undoFn,
      actionText,
      actions: [{ fn: visitAssetFn, text: visitAssetText }],
      description,
    });
  }

  get hasAction() {
    return !!this.snackbarDetails?.actionText && !!this.snackbarDetails.fn;
  }

  get hasAdditionalActions() {
    return !!this.snackbarDetails?.actions && this.snackbarDetails.actions.length > 0;
  }

  get actionText() {
    return this.snackbarDetails?.actionText;
  }

  get additionalActions() {
    return this.snackbarDetails?.actions || [];
  }

  get description() {
    return this.snackbarDetails?.description;
  }

  get snackbarsEnabled() {
    const setting = this.rootStore.userSettingsStore.userSettings.showSnackbarNotifications;
    // Default to true if not set (backwards compatible)
    return setting !== false;
  }

  get snackbarPosition() {
    const setting = this.rootStore.userSettingsStore.userSettings.snackbarPosition;
    // Default to top-center for visibility
    return setting || 'top-center';
  }

  runAdditionalAction = async (actionFn: () => void) => {
    this.reset();
    try {
      runInAction(() => {
        this.isActionInProgress = true;
      });
      await actionFn();
    } finally {
      runInAction(() => {
        this.isActionInProgress = false;
      });
    }
  };

  runSnackbarAction = async () => {
    if (this.snackbarDetails?.fn) {
      const { fn } = this.snackbarDetails;
      this.reset();
      try {
        runInAction(() => {
          this.isActionInProgress = true;
        });
        await fn();
      } finally {
        runInAction(() => {
          this.isActionInProgress = false;
        });
      }
    }
  };
}
